import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../api/client';
import { traceError, traceInfo } from '../lib/trace';

/**
 * The in-flight session probe, shared by every caller.
 *
 * The auth store is global and `loading` gates the whole authenticated
 * subtree, so a probe started by one component unmounts the others. If each
 * consumer probed on mount, that unmount/remount would restart the probe
 * forever — the app would sit on a spinner hammering /auth/me. One probe per
 * page load, shared.
 */
let probe: Promise<void> | null = null;

function probeSession(force = false): Promise<void> {
  if (probe && !force) {
    return probe;
  }

  const { setAuth, clearAuth, setLoading } = useAuthStore.getState();
  setLoading(true);

  probe = authAPI
    .getMe()
    .then(data => {
      traceInfo('auth', 'session established', {
        userId: data.user_id,
        teamId: data.team_id,
        isAdmin: data.is_admin,
      });
      setAuth(data.user_id, data.team_id, data.email, data.is_admin);
    })
    .catch((error: unknown) => {
      traceInfo('auth', 'no active session', { error });
      clearAuth();
    });

  return probe;
}

export function useAuth() {
  const { isAuthenticated, userId, teamId, email, isAdmin, loading } = useAuthStore();

  useEffect(() => {
    // Read through getState() rather than the subscribed value: this must fire
    // on the first mount only, never in response to the store changing.
    if (!useAuthStore.getState().initialized) {
      probeSession();
    }
  }, []);

  async function logout() {
    try {
      await authAPI.logout();
    } catch (error) {
      traceError('auth', 'logout request failed; clearing session anyway', { error });
    }
    probe = null;
    useAuthStore.getState().clearAuth();
    window.location.href = '/login';
  }

  return {
    isAuthenticated,
    userId,
    teamId,
    email,
    isAdmin,
    loading,
    logout,
    checkAuth: () => probeSession(true),
  };
}

/** Test seam: forget the shared probe so each test starts clean. */
export function resetAuthProbe() {
  probe = null;
}
